import { ApolloError, useLazyQuery } from '@apollo/client'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { GetFamilyAddedProductTypesQuery } from 'api/graphQL/queries/projectElements.queries'
import { ProjectElementNodeWithDrawingDesignation } from 'api/graphQL/types/projectElement.types'
import React from 'react'

interface GetFamilyAddedProductTypesResponse {
    getProjectProductFamily: {
        familyAddedProductTypes: ProjectElementNodeWithDrawingDesignation[]
    }
}

export interface FamilyAddedProductTypesApiProps {
    /** Id of the current project loaded in workspace */
    projectId?: string
    /** Callback function to set the family added product types */
    setFamilyAddedProductTypes?: (familyAddedProductTypes: ProjectElementNodeWithDrawingDesignation[]) => void
}

/**
 * A custom hook that provides functionality to fetch and manage family added product types data.
 *
 * @template T - The type of the props that extend `FamilyAddedProductTypesApiProps`.
 * @param {T} props - The properties required for the API, including the project ID and setter function.
 * @returns {Object} An object containing the `fetchFamilyAddedProductTypes` function.
 *
 * @typedef {Object} FamilyAddedProductTypesApiProps
 * @property {string} projectId - The ID of the project for which to fetch family added product types data.
 * @property {Function} setFamilyAddedProductTypes - The callback function to set the family added product types.
 *
 * @function fetchFamilyAddedProductTypes
 * @description Fetches the family added product types data for a given element ID and family node ID.
 * This function is memoized using `React.useCallback` to prevent unnecessary re-renders.
 * @param {string} elementId - The ID of the element for which to fetch the family added product types.
 * @param {string} familyNodeId - The ID of the family node for which to fetch the product types.
 * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
 */
export const useFamilyAddedProductTypesQuery = <T extends FamilyAddedProductTypesApiProps>({
    projectId = '',
    setFamilyAddedProductTypes,
}: T) => {
    const handleFetchError = (error: ApolloError) => {
        setFamilyAddedProductTypes?.([])
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-getFamilyAddedProductTypesQuery'
        )
        console.error('Error fetching family added product types:', summarizedError)
    }

    const onQueryFetchSuccess = (response: GetFamilyAddedProductTypesResponse) => {
        setFamilyAddedProductTypes?.(
            response.getProjectProductFamily.familyAddedProductTypes || []
        )
    }

    const [getFamilyAddedProductTypesQuery] = useLazyQuery<GetFamilyAddedProductTypesResponse>(
        GetFamilyAddedProductTypesQuery,
        {
            fetchPolicy: 'no-cache',
            onCompleted: onQueryFetchSuccess,
            onError: handleFetchError,
        }
    )

    /**
     * Fetches the family added product types data for a given element ID and family node ID.
     *
     * This function is memoized using `React.useCallback` to prevent unnecessary re-renders.
     *
     * @param {string} elementId - The ID of the element for which to fetch the family added product types.
     * @param {string} familyNodeId - The ID of the family node for which to fetch the product types.
     * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
     */
    const fetchFamilyAddedProductTypes = React.useCallback(
        async (elementId: string, familyNodeId: string): Promise<void> => {
            const queryVariables = {
                projectId,
                elementId,
                familyNodeId,
            }
            return getFamilyAddedProductTypesQuery({
                variables: queryVariables,
            })
        },
        [projectId, getFamilyAddedProductTypesQuery]
    )

    return { fetchFamilyAddedProductTypes }
}